import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment';
import { Messages } from 'src/app/core/endpointsAndGeneric-constants/messages';
import { SharedService } from '../../common-services/shared.service';

@Component({
  selector: 'app-customize-field-popup',
  templateUrl: './customize-field-popup.component.html',
  styleUrls: ['./customize-field-popup.component.scss'],
})
export class CustomizeFieldPopupComponent implements OnInit {
  channel: FormGroup;
  behaviourList: {name: string, value: string}[] = [];
  templateConfig: any;
  timeZoneNames: string[];
  formatList: {name: string, value: string}[] = [];
  deviceFieldList: {name: string, value: string}[] = [];
  fieldLengthLimit: any;

  readonly MESSAGES = Messages;

  constructor(
    public dialogRef: MatDialogRef<CustomizeFieldPopupComponent>,
    private _fb: FormBuilder,
    private _sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public data
  ) {
    this.timeZoneNames = moment.tz.names();
    this.channel = this._fb.group({
      name: [null, Validators.required],
      description: [null],
      startValue: [null],
      endValue: [null],
      type: [null, Validators.required],
      deviceField: [null],
      format: [null],
      timeZone: [null],
      columnName: [null],
      inputValue: [null],
      stepValue: [null],
      cumulative: [null],
      carryForward: [null],
      trendSlope: [null],
      seasonalityAmplitude: [null],
      initialValue: [null],
      noiseLevel: [null],
      trendNoiseLevel: [null],
      lowerThreshold: [null],
      upperThreshold: [null],
      noOfDecimals: [null],
      behaviour: [null],
      fieldLength: [null]
    });
  }

  ngOnInit(): void {
    this.fieldLengthLimit = this._sharedService?.getAttributes('templateConfigurations')?.fieldLengthLimit ? this._sharedService?.getAttributes('templateConfigurations')?.fieldLengthLimit : {};
    if (this.data?.simulationData?.attributes) {
      this.channel?.patchValue(this.data?.simulationData?.attributes);
      this._validateFeilds(this.data?.simulationData?.attributes?.type);
    }
    if(this.data?.pageType === 'Device-Attach') {
      this._disableFields();
    }
    this.channel.get('type')?.valueChanges?.subscribe((type: string) => {
      this._initializeForm(type);
    });
    this.templateConfig = this._sharedService?.getAttributes('templateConfigurations');
    this._getListsFromConfig();
  }

  /**
   * Numbers only
   * @param event
   * @param [type]
   * @param [value]
   * @returns true if only
   */
  numberOnly(event: any, type?: string, value?: number): boolean {
    const charCode = event?.which ? event?.which : event?.keyCode;
    if (type === 'countryCode' && charCode === 45) {
      return true;
    }
    if (
      type === 'exponent' &&
      (charCode === 101 ||
        charCode === 69 ||
        (value === null && charCode === 46))
    ) {
      return false;
    }
    if (
      type === 'nonNegative' &&
      (charCode === 101 || charCode === 69 || charCode === 45)
    ) {
      return false;
    }
    if (!type && charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  /**
   * Inputs validator
   * @param event
   * @param type
   */
  inputValidator(event: any, type?: string) {
    let pattern;
    if (type === 'inputValue') {
      pattern = /^[a-zA-Z0-9_.+-\s]*$/;
      if (!pattern?.test(event?.target?.value)) {
        event.target.value = event?.target?.value?.replace(
          /[^a-zA-Z0-9_.+-]/g,
          ''
        );
      }
    } else {
      pattern = /^[a-zA-Z0-9_-\s]*$/;
      if (!pattern.test(event?.target?.value)) {
        event.target.value = event?.target?.value?.replace(
          /[^a-zA-Z0-9_-\s]/g,
          ''
        );
      }
    }
  }

  /**
   * Closes customize field popup component
   * @param [type]
   */
  close(type = 'default') {
    if (type === 'default') {
      this.dialogRef?.close();
    } else {
      this.data.simulationData.isEdited = true;
      this.data.simulationData.attributes = Object?.fromEntries(
        Object?.entries(this.channel?.getRawValue())
      );
      this.dialogRef?.close(this.data?.simulationData);
    }
  }

  /**
   * Disables fields
   */
  private _disableFields() {
    this.channel?.get('name')?.disable();
    this.channel?.get('description')?.disable();
    this.channel?.get('type')?.disable();
  }

  /**
   * Gets lists from config
   */
  private _getListsFromConfig() {
    this.formatList = this.templateConfig?.formatList ? this.templateConfig?.formatList : [];
    this.behaviourList = this.templateConfig?.behaviourList ? this.templateConfig?.behaviourList : [];
    this.deviceFieldList = this.templateConfig?.deviceFieldList ? this.templateConfig?.deviceFieldList : [];
  }

  /**
   * Validates feilds
   * @param type
   */
  private _validateFeilds(type: string) {
    interface FieldMap {
      [key: string]: string[];
    }
    const requiredFields: FieldMap = {
      CSV: ['columnName', 'behaviour'],
      ORDERED: ['startValue', 'endValue', 'stepValue', 'behaviour'],
      RANDOM: ['startValue', 'endValue'],
      CONSTANT: ['inputValue'],
      TIMESERIES: [
        'trendSlope',
        'seasonalityAmplitude',
        'initialValue',
        'noiseLevel',
        'trendNoiseLevel',
      ],
      TIMESTAMP: ['format', 'timeZone'],
      DEVICEINFO: ['deviceField'],
      UNIQUEID: ['fieldLength']

    };
    Object.entries(requiredFields)?.forEach(([key, fields]) => {
      if (type === 'ORDERED' && key === 'RANDOM') {
        return;
      }
      const shouldValidate = type === key;
      fields?.forEach((field) => {
        if (shouldValidate) {
          this.channel.get(field)?.setValidators(Validators.required);
          this.channel.get(field)?.updateValueAndValidity();
        } else {
          this.channel.get(field)?.removeValidators(Validators.required);
          this.channel.get(field)?.updateValueAndValidity();
        }
        this.channel.get(field)?.updateValueAndValidity();
      });
    });
  }

  /**
   * Initializes form
   * @param type
   */
  private _initializeForm(type) {
    this._validateFeilds(type);
    this.channel.get('carryForward')?.reset();
    this.channel.get('startValue')?.reset();
    this.channel.get('endValue')?.reset();
    this.channel.get('columnName')?.reset();
    this.channel.get('inputValue')?.reset();
    this.channel.get('stepValue')?.reset();
    this.channel.get('cumulative')?.reset();
    this.channel.get('trendSlope')?.reset();
    this.channel.get('seasonalityAmplitude')?.reset();
    this.channel.get('initialValue')?.reset();
    this.channel.get('noiseLevel')?.reset();
    this.channel.get('trendNoiseLevel')?.reset();
    this.channel.get('lowerThreshold')?.reset();
    this.channel.get('upperThreshold')?.reset();
    this.channel.get('noOfDecimals')?.reset();
    this.channel.get('format')?.reset();
    this.channel.get('timeZone')?.reset();
    this.channel.get('deviceField')?.reset();
    this.channel.get('behaviour')?.reset();
    this.channel.get('fieldLength')?.reset();
    this.channel
      .get('startValue')
      .valueChanges.subscribe((startValue: number) => {
        const endValue = this.channel.get('endValue')?.value;
        const type = this.channel.get('type')?.value;
        if (type === 'ORDERED') {
          this.channel
            .get('stepValue')
            .setValidators([
              Validators.required,
              this.stepValueValidator(startValue, endValue),
            ]);
          this.channel.get('stepValue')?.updateValueAndValidity();
        }
      });
    this.channel.get('endValue')?.valueChanges?.subscribe((endValue: number) => {
      const startValue = this.channel.get('startValue')?.value;
      const type = this.channel.get('type')?.value;
      if (type === 'ORDERED') {
        this.channel
          .get('stepValue')
          .setValidators([
            Validators.required,
            this.stepValueValidator(startValue, endValue),
          ]);
        this.channel.get('stepValue')?.updateValueAndValidity();
      }
    });
  }

  /**
   * Steps value validator
   * @param startValue
   * @param endValue
   * @returns value validator
   */
  stepValueValidator(startValue: number, endValue: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const stepValue = control.value;
      if (startValue !== null && endValue !== null && stepValue !== null) {
        if (startValue > endValue && stepValue >= 0) {
          return {
            invalidStep: true,
            message:
              'Step value must be negative when start value is greater than end value.',
          };
        } else if (startValue < endValue && stepValue <= 0) {
          return {
            invalidStep: true,
            message:
              'Step value must be positive when start value is less than end value.',
          };
        }
      }
      return null;
    };
  }
}
